import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Mail, Globe, FolderTree, Pencil, X, Save } from 'lucide-react';
import AdminLayout from '../../components/Layout/AdminLayout';
import Toast, { useToasts } from '../../components/Layout/Toast';
import { monitorApi } from '../../services/api';
import { Recipient, MonitorGroup } from '../../types';

const AdminRecipientsPage: React.FC = () => {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [groups, setGroups] = useState<MonitorGroup[]>([]);
  const [email, setEmail] = useState('');
  const [scope, setScope] = useState<'global' | 'group'>('global');
  const [groupId, setGroupId] = useState<string>('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editScope, setEditScope] = useState<'global' | 'group'>('global');
  const [editGroupId, setEditGroupId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const toast = useToasts();

  const load = useCallback(async () => {
    try {
      const [r, g] = await Promise.all([monitorApi.getRecipients(), monitorApi.getAdminGroups()]);
      setRecipients(r.data);
      setGroups(g.data);
    } catch { toast.error('Failed to load recipients'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (scope === 'group' && !groupId) { toast.error('Select a group'); return; }
    try {
      await monitorApi.addRecipient({ email, scope, group_id: scope === 'group' ? Number(groupId) : null });
      setEmail(''); setGroupId('');
      toast.success('Recipient added');
      load();
    } catch (err: any) { toast.error(err.response?.data?.error || 'Failed to add recipient'); }
  };

  const remove = async (r: Recipient) => {
    if (!window.confirm(`Remove recipient ${r.email}?`)) return;
    try { await monitorApi.deleteRecipient(r.id); toast.success('Recipient removed'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const startEdit = (r: Recipient) => {
    setEditingId(r.id);
    setEditEmail(r.email);
    setEditScope(r.scope);
    setEditGroupId(r.group_id ? String(r.group_id) : '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditEmail('');
    setEditScope('global');
    setEditGroupId('');
  };

  const saveEdit = async (r: Recipient) => {
    if (editScope === 'group' && !editGroupId) {
      toast.error('Select a group');
      return;
    }
    try {
      await monitorApi.updateRecipient(r.id, {
        email: editEmail,
        scope: editScope,
        group_id: editScope === 'group' ? Number(editGroupId) : null,
      });
      toast.success('Recipient updated');
      cancelEdit();
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Update failed');
    }
  };

  const input = 'w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40';

  return (
    <AdminLayout>
      <Toast toasts={toast.toasts} onDismiss={toast.dismiss} />
      <div className="max-w-4xl mx-auto p-8">
        <h1 className="text-2xl font-bold text-slate-800 mb-1">Alert Recipients</h1>
        <p className="text-sm text-slate-500 mb-6">
          Global recipients receive alerts for every monitor. Group recipients receive alerts for monitors in their group.
          Per-monitor recipients are configured on each monitor.
        </p>

        <form onSubmit={add} className="bg-white rounded-xl border border-slate-200 p-5 mb-6 grid grid-cols-1 md:grid-cols-[1.5fr_1fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Email</label>
            <input type="email" className={input} value={email} onChange={e => setEmail(e.target.value)} required placeholder="oncall@emerson.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Scope</label>
            <select className={input} value={scope} onChange={e => setScope(e.target.value as any)}>
              <option value="global">Global</option>
              <option value="group">Group</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Group</label>
            <select className={input} value={groupId} onChange={e => setGroupId(e.target.value)} disabled={scope !== 'group'}>
              <option value="">—</option>
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <button type="submit" className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-brand text-white rounded-md hover:bg-brand-dark h-[38px]">
            <Plus size={16} /> Add
          </button>
        </form>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="px-5 py-16 text-center text-slate-400">Loading…</div>
          ) : recipients.length === 0 ? (
            <div className="px-5 py-16 text-center text-slate-400">No recipients yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recipients.map(r => (
                <div key={r.id} className="flex items-center justify-between px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center"><Mail size={18} /></div>
                    {editingId === r.id ? (
                      <div className="space-y-2">
                        <input
                          type="email"
                          className={`${input} min-w-[260px]`}
                          value={editEmail}
                          onChange={e => setEditEmail(e.target.value)}
                          placeholder="oncall@emerson.com"
                        />
                        <div className="flex items-center gap-2">
                          <select className={input} value={editScope} onChange={e => setEditScope(e.target.value as 'global' | 'group')}>
                            <option value="global">Global</option>
                            <option value="group">Group</option>
                          </select>
                          <select className={input} value={editGroupId} onChange={e => setEditGroupId(e.target.value)} disabled={editScope !== 'group'}>
                            <option value="">—</option>
                            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                          </select>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="font-medium text-slate-800">{r.email}</div>
                        <div className="text-xs text-slate-400 flex items-center gap-1">
                          {r.scope === 'global' ? <><Globe size={12} /> Global</> : <><FolderTree size={12} /> Group: {r.group_name || '—'}</>}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {editingId === r.id ? (
                      <>
                        <button onClick={() => saveEdit(r)} className="p-1.5 text-slate-500 hover:text-brand hover:bg-slate-100 rounded" title="Save"><Save size={16} /></button>
                        <button onClick={cancelEdit} className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded" title="Cancel"><X size={16} /></button>
                      </>
                    ) : (
                      <button
                        onClick={() => startEdit(r)}
                        disabled={editingId !== null}
                        className="p-1.5 text-slate-500 hover:text-brand hover:bg-slate-100 rounded disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-500 disabled:cursor-not-allowed"
                        title={editingId !== null ? 'Finish current edit first' : 'Edit'}
                      >
                        <Pencil size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => remove(r)}
                      disabled={editingId !== null && editingId !== r.id}
                      className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-slate-100 rounded disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-500 disabled:cursor-not-allowed"
                      title={editingId !== null && editingId !== r.id ? 'Finish current edit first' : 'Delete'}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminRecipientsPage;
